import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PostCategory {
	id: number;
	name: string;
	slug: string;
	description: string;
	color: string;
	icon: string;
	order: number;
	is_active: boolean;
}

export interface PostTag {
	id: number;
	name: string;
	slug: string;
}

export interface PostImage {
	id: number;
	blog_id: number;
	image_path: string;
	image_name: string;
	caption: string;
	order: number;
}

export interface Post {
	id: number;
	user_id: number;
	title: string;
	slug: string;
	short_description: string;
	content: string;
	featured_image: string;
	featured_image_url: string;
	og_image?: string;
	og_image_url?: string;
	meta_title?: string;
	meta_description?: string;
	meta_keywords?: string;
	reading_time_minutes: number;
	is_published: boolean;
	is_featured: boolean;
	featured_order: number;
	published_at?: string;
	views_count: number;
	likes_count: number;
	comments_count: number;
	created_at: string;
	updated_at: string;
	tags?: PostTag[];
	images?: PostImage[];
	user?: any;
}

export interface PostComment {
	id: number;
	blog_id: number;
	parent_id?: number;
	name: string;
	email: string;
	comment: string;
	is_approved: boolean;
	created_at: string;
	replies?: PostComment[];
}

@Injectable({
	providedIn: 'root',
})
export class PostService {
	private http = inject(HttpClient);
	private apiUrl = environment.apiUrl;

	// Public Blog APIs
	getPublishedBlogs(params?: any): Observable<any> {
		return this.http.get(`${this.apiUrl}/posts/published`, { params });
	}

	getBlogBySlug(slug: string): Observable<any> {
		return this.http.get(`${this.apiUrl}/posts/slug/${slug}`);
	}

	getBlogsByCategory(categorySlug: string, params?: any): Observable<any> {
		return this.http.get(`${this.apiUrl}/posts/category/${categorySlug}`, { params });
	}

	getBlogsByTag(tagSlug: string, params?: any): Observable<any> {
		return this.http.get(`${this.apiUrl}/posts/tag/${tagSlug}`, { params });
	}

	getFeaturedBlogs(): Observable<any> {
		return this.http.get(`${this.apiUrl}/posts/featured`);
	}

	getTrendingBlogs(): Observable<any> {
		return this.http.get(`${this.apiUrl}/posts/trending`);
	}

	getRelatedBlogs(blogId: number): Observable<any> {
		return this.http.get(`${this.apiUrl}/posts/${blogId}/related`);
	}

	trackView(blogId: number): Observable<any> {
		return this.http.post(`${this.apiUrl}/posts/${blogId}/view`, {});
	}

	addReaction(blogId: number, reactionType: string): Observable<any> {
		return this.http.post(`${this.apiUrl}/posts/${blogId}/react`, { reaction_type: reactionType });
	}

	getComments(blogId: number): Observable<any> {
		return this.http.get(`${this.apiUrl}/posts/${blogId}/comments`);
	}

	addComment(blogId: number, comment: any): Observable<any> {
		return this.http.post(`${this.apiUrl}/posts/${blogId}/comments`, comment);
	}

	// Category APIs
	getCategories(): Observable<any> {
		return this.http.get(`${this.apiUrl}/categories`);
	}

	getTags(): Observable<any> {
		return this.http.get(`${this.apiUrl}/tags`);
	}

	// Admin Blog APIs
	getAllBlogs(params?: any): Observable<any> {
		return this.http.get(`${this.apiUrl}/admin/posts`, { params });
	}

	getBlogById(id: number): Observable<any> {
		return this.http.get(`${this.apiUrl}/admin/posts/${id}`);
	}

	createBlog(formData: FormData): Observable<any> {
		return this.http.post(`${this.apiUrl}/admin/posts`, formData);
	}

	updateBlog(id: number, formData: FormData): Observable<any> {
		return this.http.post(`${this.apiUrl}/admin/posts/${id}`, formData);
	}

	deleteBlog(id: number): Observable<any> {
		return this.http.delete(`${this.apiUrl}/admin/posts/${id}`);
	}

	togglePublish(id: number): Observable<any> {
		return this.http.patch(`${this.apiUrl}/admin/posts/${id}/toggle-publish`, {});
	}

	deleteImage(blogId: number, imageId: number): Observable<any> {
		return this.http.delete(`${this.apiUrl}/admin/posts/${blogId}/images/${imageId}`);
	}

	getBlogAnalytics(): Observable<any> {
		return this.http.get(`${this.apiUrl}/admin/posts/analytics`);
	}

	getBlogStatistics(id: number): Observable<any> {
		return this.http.get(`${this.apiUrl}/admin/posts/${id}/statistics`);
	}

	// Admin Category APIs
	createCategory(category: Partial<PostCategory>): Observable<any> {
		return this.http.post(`${this.apiUrl}/admin/categories`, category);
	}

	updateCategory(id: number, category: Partial<PostCategory>): Observable<any> {
		return this.http.put(`${this.apiUrl}/admin/categories/${id}`, category);
	}

	deleteCategory(id: number): Observable<any> {
		return this.http.delete(`${this.apiUrl}/admin/categories/${id}`);
	}
}
